import { request } from '@/api/apiUtils';
import { createEntityQuery } from '@/data/createEntityQuery';
import type { EntityDefinition, FetcherEntry } from '@/data/types';
import type { IProject, ResponseType } from '@/types';

/** Root: query-key prefix for this entity's slice of the cache (ADR 0010) */
export const PROJECTS_ROOT = ['projects'] as const;

interface ProjectDetailParams {
    projectId: number;
    version?: string;
}

interface ProjectsListParams {
    companyId: number;
    version?: string;
}

const fetchers = {
    detail: {
        key: ({ projectId, version }: ProjectDetailParams) => [...PROJECTS_ROOT, 'detail', { projectId, version }] as const,
        fetch: async ({ projectId, version }: ProjectDetailParams): Promise<IProject> => {
            const params = new URLSearchParams();
            if (version) params.append('version', version);
            const endpoint = `projects/${projectId}${params.toString() ? `?${params}` : ''}`;
            const data: { data: IProject } = await request(endpoint);
            return data.data;
        },
    },
    list: {
        key: ({ companyId, version }: ProjectsListParams) => [...PROJECTS_ROOT, 'list', { companyId, version }] as const,
        fetch: async ({ companyId, version }: ProjectsListParams): Promise<IProject[]> => {
            const params = new URLSearchParams();
            params.set('company_id', String(companyId));
            if (version) params.set('version', version);
            const response: ResponseType<IProject[]> = await request(`projects?${params.toString()}`);
            return response.data;
        },
    },
    // biome-ignore lint/suspicious/noExplicitAny: required for generic type inference
} satisfies Record<string, FetcherEntry<any, any>>;

export const projectsEntity: EntityDefinition<typeof fetchers> = {
    name: 'projects',
    roots: [PROJECTS_ROOT],
    fetchers,
};

export const useProjectDetail = createEntityQuery(projectsEntity, 'detail');
export const useProjectsList = createEntityQuery(projectsEntity, 'list');

export type { ProjectDetailParams, ProjectsListParams };
